import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { ChatMessage } from "@/components/chat-panel";

/**
 * FullscreenChatOverlay — floating, low-opacity chat overlay rendered on top
 * of the video player when fullscreen mode is active.
 *
 * - Position: fixed bottom-left, 20px margin
 * - Visuals: backdrop-filter blur(8px), rgba(0,0,0,0.4) bg, white text
 * - Opacity: 0.55 base, readable but transparent
 * - Auto-fade: each message fades out after 4 seconds
 * - pointer-events: none — never blocks video clicks
 * - Read-only: no input
 */
export default function FullscreenChatOverlay({ messages }: { messages: ChatMessage[] }) {
  const [visibleMessages, setVisibleMessages] = useState<{ msg: ChatMessage; key: string }[]>([]);
  const lastCountRef = useRef(0);

  // When a new message arrives, add it to visible and auto-remove after 4s
  useEffect(() => {
    if (messages.length > lastCountRef.current) {
      const newMsgs = messages.slice(lastCountRef.current);
      newMsgs.forEach((msg) => {
        if (msg.type === "system") return;
        const key = `${msg.id}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
        setVisibleMessages((prev) => [...prev, { msg, key }].slice(-5)); // keep last 5
        setTimeout(() => {
          setVisibleMessages((prev) => prev.filter((m) => m.key !== key));
        }, 4000);
      });
    }
    lastCountRef.current = messages.length;
  }, [messages]);

  // Don't render if no visible messages
  if (visibleMessages.length === 0) return null;

  return (
    <div
      className="fixed bottom-5 left-5 z-50 max-w-[320px] space-y-1.5 pointer-events-none"
      style={{ opacity: 0.55 }}
      aria-hidden="true"
    >
      <AnimatePresence>
        {visibleMessages.map(({ msg, key }) => (
          <motion.div
            key={key}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="rounded-lg px-3 py-2 text-white text-xs backdrop-blur-[8px]"
            style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
          >
            <span className="text-[10px] font-medium text-[#78B4FF] mr-1.5">{msg.userName}</span>
            {msg.gifUrl ? (
              <img src={msg.gifPreview || msg.gifUrl} alt="GIF" className="rounded-md max-w-[180px] max-h-[120px] object-contain mt-1" />
            ) : (
              <span>{msg.content}</span>
            )}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
